/**
 * Service-worker registration and the "a new version is ready" signal.
 *
 * This is the front half of the migration story. The app updates on a prompt,
 * not silently: when a new build is waiting we surface it, and applying it
 * reloads the page — a clean load on the new bundle, which is what lets boot
 * detect and run any new migrations in order. Silent swaps would risk running
 * old code against a page mid-session.
 */
import { reactive } from 'vue'

export const updateState = reactive({
  available: false, // a newer build is waiting
  offlineReady: false, // first install cached; usable offline
})

let applyFn = null
let registration = null

export async function initPWA() {
  // The virtual module only exists in a real build; importing it lazily keeps
  // dev and the SSR test runner clear of it.
  if (!import.meta.env.PROD) return
  const { registerSW } = await import('virtual:pwa-register')
  applyFn = registerSW({
    immediate: true,
    onNeedRefresh() {
      updateState.available = true
    },
    onOfflineReady() {
      updateState.offlineReady = true
    },
    onRegisteredSW(_url, reg) {
      registration = reg
      if (reg) watchForUpdates(reg)
    },
  })
}

/** How often to look while the app is on screen. */
export const CHECK_EVERY_MS = 60 * 1000

/**
 * When to ask the server whether a new build is out. Asking is one conditional
 * request for sw.js — a few hundred bytes and a 304 when nothing changed; the
 * new files are only fetched when there is something new.
 *
 *   - the moment the app comes back into view (tab switched to, app reopened on
 *     a phone) or the device comes back online: that is when a new version
 *     matters, and it costs nothing to ask then
 *   - every minute while it stays in view, so a deploy shows within a minute
 *   - never while hidden: nobody is there to press the banner, and a phone
 *     should not wake its radio for a tab in the background
 *
 * Checks closer together than a few seconds (focus and visibility often fire
 * together) are one check. Returns a function that stops watching.
 */
export function watchForUpdates(reg, { every = CHECK_EVERY_MS, gap = 5000, doc = document, win = window } = {}) {
  let last = 0
  const check = () => {
    if (doc.visibilityState === 'hidden') return
    const now = Date.now()
    if (now - last < gap) return
    last = now
    reg.update().catch(() => {})
  }
  const onVisible = () => doc.visibilityState === 'visible' && check()
  doc.addEventListener('visibilitychange', onVisible)
  win.addEventListener('focus', check)
  win.addEventListener('online', check)
  const timer = setInterval(check, every)
  return () => {
    clearInterval(timer)
    doc.removeEventListener('visibilitychange', onVisible)
    win.removeEventListener('focus', check)
    win.removeEventListener('online', check)
  }
}

/** Apply the waiting update and reload onto it. */
export function applyUpdate() {
  if (applyFn) return applyFn(true)
  location.reload()
}

/**
 * Reload onto the newest build there is, from a page that cannot wait for the
 * banner — the "update OKANE to continue" screen, drawn before the app (and
 * its update check) ever starts.
 *
 * A plain reload is not enough, and was a trap: updates wait for a prompt, so
 * a new service worker sits installed but *waiting* while this page is open,
 * and the old one goes on serving the old bundle to every reload. So: ask the
 * server for the newest worker, wait for it to install, tell it to take over
 * (`SKIP_WAITING`, which the generated worker obeys), and reload once it has.
 * With no worker, or none newer, it is an ordinary reload.
 */
export async function reloadToLatest({
  sw = globalThis.navigator?.serviceWorker,
  reload = () => location.reload(),
  wait = 10_000,
  settle = 3_000,
} = {}) {
  const reg = await Promise.resolve(sw?.getRegistration?.()).catch(() => null)
  if (!reg) return reload()
  await Promise.resolve(reg.update?.()).catch(() => {})
  const worker = reg.waiting ?? (await installed(reg, wait))
  if (!worker) return reload()
  let gone = false
  const go = () => {
    if (gone) return
    gone = true
    reload()
  }
  sw.addEventListener('controllerchange', go, { once: true })
  worker.postMessage({ type: 'SKIP_WAITING' })
  // A worker that never takes over still gets a reload, rather than a dead button.
  setTimeout(go, settle)
}

/** The worker being installed, once it has — or null if there is none, or it fails. */
function installed(reg, wait) {
  const worker = reg.installing
  if (!worker) return Promise.resolve(null)
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(reg.waiting ?? null), wait)
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed' || worker.state === 'redundant') {
        clearTimeout(timer)
        resolve(worker.state === 'installed' ? (reg.waiting ?? worker) : null)
      }
    })
  })
}

/**
 * Whether a newer build is waiting (or installing) right now — asked when a
 * code arrives (a QR code opened by the camera, a sign-in link): an old cached
 * build would read it the old way, so the newest is loaded first.
 */
export async function newerBuild({ sw = globalThis.navigator?.serviceWorker } = {}) {
  const reg = await Promise.resolve(sw?.getRegistration?.()).catch(() => null)
  if (!reg) return false
  await Promise.resolve(reg.update?.()).catch(() => {})
  return !!(reg.waiting || reg.installing)
}
